import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import * as OnboardingAPI from '../../../API/OnboardingAPI'
import { immer } from 'zustand/middleware/immer'
import * as MainContainerEntity from '../../../Entities/MainContainerTypes'

const initialState = {
    currentCountryCode: '',
    currentStateCode: '',
    createSurveyTemplateData: {},
    createSurveyTemplateLoading: false,
    createSurveyTemplateError: '',

    getSurveyTemplateData: [],
    getSurveyTemplateLoading: false,
    getSurveyTemplateError: '',

    updateSurveyTemplateData: null,
    updateSurveyTemplateLoading: false,
    updateSurveyTemplateError: '',

    createBoardCompositionData: null,
    createBoardCompositionLoading: false,
    createBoardCompositionError: '',

    getBoardCompositionData: [],
    getBoardCompositionLoading: false,
    getBoardCompositionError: '',

    createOrganizationChartData: [],
    createOrganizationChartLoading: false,
    createOrganizationChartError: '',

    editOrganizationChartNodeData: [],
    editOrganizationChartNodeLoading: false,
    editOrganizationChartNodeError: '',

    getOrganizationChartData: [],
    getOrganizationChartLoading: false,
    getOrganizationChartError: '',

    createOrgRegulationData: null,
    createOrgRegulationLoading: false,
    createOrgRegulationError: '',

    getOrgRegulationData: null,
    getOrgRegulationLoading: false,
    getOrgRegulationError: '',

    orgTypeData: [],
    orgTypeLoading: false,
    orgTypeDataError: '',

    createCompanyProfileData: {},
    createCompanyProfileLoading: false,
    createCompanyProfileError: '',

    getCompanyProfileData: {},
    getCompanyProfileLoading: false,
    getCompanyProfileError: '',

    getCountryData: [],
    getCountryLoading: false,
    getCountryError: '',

    getStateData: [],
    getStateLoading: false,
    getStateError: '',

    getCityData: [],
    getCityLoading: false,
    getCityError: ''
}

const OnboardingStore = (set, get) => ({
    ...initialState,
    setCurrentCountryCode: (data) => {
        set({ currentCountryCode: data })
    },
    setCurrentStateCode: (data) => {
        set({ currentStateCode: data })
    },
    createSurveyTemplate: async (data, loginId) => {
        set({ createSurveyTemplateLoading: true, createSurveyTemplateError: '' })
        try {
            const response = await OnboardingAPI.createSurveyTemplate(data, loginId)
            if (response.data) {
                set({ createSurveyTemplateLoading: false, createSurveyTemplateData: response.data })
                return response
            } else {
                set({ createSurveyTemplateLoading: false, createSurveyTemplateData: {} })
            }
        } catch (error) {
            set({ createSurveyTemplateError: error.message || 'An error occurred', createSurveyTemplateLoading: false })
        }
    },
    updateSurveyTemplate: async (data, loginId, organizationId) => {
        set({ updateSurveyTemplateLoading: true, updateSurveyTemplateError: '' })
        try {
            const response = await OnboardingAPI.updateSurveyTemplate(data, loginId, organizationId)
            if (response.data) {
                set({ updateSurveyTemplateLoading: false, updateSurveyTemplateData: response.data })
                return response
            } else {
                set({ updateSurveyTemplateLoading: false, updateSurveyTemplateData: {} })
            }
        } catch (error) {
            set({ updateSurveyTemplateError: error.message || 'An error occurred', updateSurveyTemplateLoading: false })
        }
    },

    createBoardComposition: async (data, loginId) => {
        set({ createBoardCompositionLoading: true, createBoardCompositionError: '' })
        try {
            const response = await OnboardingAPI.createBoardComposition(data, loginId)
            if (response) {
                set({ createBoardCompositionLoading: false, createBoardCompositionData: response })
                return response
            } else {
                set({ createBoardCompositionLoading: false, createBoardCompositionData: {} })
            }
        } catch (error) {
            set({ createBoardCompositionError: error.message || 'An error occurred', createBoardCompositionLoading: false })
        }
    },

    createOrganizationChart: async (data, loginId, orgId) => {
        set({ createOrganizationChartLoading: true, createOrganizationChartError: '' })
        try {
            const response = await OnboardingAPI.createOrganizationChart(data, loginId, orgId)
            if (response.data) {
                set({ createOrganizationChartLoading: false, createOrganizationChartData: response })
                return response
            } else {
                set({ createOrganizationChartLoading: false, createOrganizationChartData: [] })
                return response
            }
        } catch (error) {
            set({ createOrganizationChartError: error.message || 'An error occurred', createOrganizationChartLoading: false })
        }
    },

    editOrganizationChartNode: async (data) => {
        set({ editOrganizationChartNodeLoading: true, editOrganizationChartNodeError: '' })
        try {
            const response = await OnboardingAPI.editOrganizationChartNode(data)
            if (response.data) {
                set({ editOrganizationChartNodeLoading: false, editOrganizationChartNodeData: response })
                return response
            } else {
                set({ editOrganizationChartNodeLoading: false, editOrganizationChartNodeData: [] })
                return response
            }
        } catch (error) {
            set({ editOrganizationChartNodeError: error.message || 'An error occurred', editOrganizationChartNodeLoading: false })
        }
    },

    createOrgRegulation: async (data, loginId, orgId) => {
        set((state) => ({ ...state, createOrgRegulationLoading: true }))
        try {
            const response = await OnboardingAPI.createOrgRegulation(data, loginId, orgId)
            if (response.data) {
                set((state) => ({ ...state, createOrgRegulationLoading: false, createOrgRegulationData: response.data }))
                return response
            } else {
                set({ createOrgRegulationLoading: false, createOrgRegulationData: {} })
            }
        } catch (error) {
            set((state) => ({ ...state, createOrgRegulationError: error.message || 'An error occurred', createOrgRegulationLoading: false }))
        }
    },

    getBoardComposition: async (orgId) => {
        set({ getBoardCompositionLoading: true })
        try {
            const response = await OnboardingAPI.getBoardComposition(orgId)
            if (response.data) {
                set({ getBoardCompositionLoading: false, getBoardCompositionData: response.data })
                return response
            } else {
                set({ getBoardCompositionLoading: false, getBoardCompositionData: [] })
            }
        } catch (error) {
            set({ getBoardCompositionError: error.message || 'An error occurred', getBoardCompositionLoading: false })
        }
    },

    getOrganizationChart: async (orgId, loginId) => {
        set({ getOrganizationChartLoading: true })
        try {
            const response = await OnboardingAPI.getOrganizationChart(orgId, loginId)
            if (response.data) {
                set({ getOrganizationChartLoading: false, getOrganizationChartData: response.data })
                return response
            } else {
                set({ getOrganizationChartLoading: false, getOrganizationChartData: [] })
            }
        } catch (error) {
            set({ getOrganizationChartError: error.message || 'An error occurred', getOrganizationChartLoading: false })
        }
    },

    getUserDetailsById: async (userId) => {
        set({ getUserDetailsByIdLoading: true })
        try {
            const response = await OnboardingAPI.getUserDetailsById(userId)
            if (response.data) {
                set({ getUserDetailsByIdLoading: false, getUserDetailsByIdData: response.data })
                return response
            } else {
                set({ getUserDetailsByIdLoading: false, getUserDetailsByIdData: [] })
            }
        } catch (error) {
            set({ getUserDetailsByIdError: error.message || 'An error occurred', getUserDetailsByIdLoading: false })
        }
    },

    getOrgRegulation: async (orgId, loginId, typeId) => {
        set((state) => ({ ...state, getOrgRegulationLoading: true }))
        try {
            const response = await OnboardingAPI.getOrgRegulation(orgId, loginId, typeId)
            set((state) => ({ ...state, getOrgRegulationLoading: false, getOrgRegulationData: response.data || null }))
        } catch (error) {
            set((state) => ({ ...state, orgRegulationError: error.message || 'An error occurred', getOrgRegulationLoading: false }))
        }
    },
    getOrgloadOn: () => {
        set((state) => ({ ...state, getOrgRegulationLoading: true }))
    },
    getOrgloadOff: () => {
        set((state) => ({ ...state, getOrgRegulationLoading: false }))
    }
})

const OnboardingStoreForSurvey = (set, get) => ({
    ...initialState,

    getSurveyTemplate: async (vertexId, templateId) => {
        set({ getSurveyTemplateLoading: true })
        try {
            const response = await OnboardingAPI.getSurveyTemplate(vertexId, templateId)
            if (response.data) {
                set({ getSurveyTemplateLoading: false, getSurveyTemplateData: MainContainerEntity.getSurveyTemplateDataNew })
            } else {
                set({ initialState, getSurveyTemplateLoading: false, getSurveyTemplateData: initialState.getSurveyTemplateData })
            }
        } catch (error) {
            set({ getSurveyTemplateError: error.message || 'An error occurred', getSurveyTemplateLoading: false })
        }
    },
    getOrgType: async (orgId) => {
        set({ orgTypeLoading: true })
        try {
            const response = await OnboardingAPI.getOrgType(orgId)
            if (response.data) {
                set({ orgTypeLoading: false, orgTypeData: response.data })
            } else {
                set({ orgTypeLoading: false, orgTypeData: [], tableBodyData: initialState.tableBodyData })
            }
        } catch (error) {
            set({ orgTypeDataError: error.message || 'An error occurred', orgTypeLoading: false })
        }
    }
})

const ProfileScreenStore = (set, get) => ({
    ...initialState,

    createCompanyProfile: async (data, loginId) => {
        set({ createCompanyProfileLoading: true, createCompanyProfileError: '' })
        try {
            const response = await OnboardingAPI.createCompanyProfile(data, loginId)
            if (response) {
                set({ createCompanyProfileLoading: false, createCompanyProfileData: response })
                return response
            } else {
                set({ createCompanyProfileLoading: false, createCompanyProfileData: {} })
            }
        } catch (error) {
            set({ createCompanyProfileError: error.message || 'An error occurred', createCompanyProfileLoading: false })
        }
    },

    getCompanyProfile: async (orgId) => {
        set({ getCompanyProfileLoading: true })
        try {
            const response = await OnboardingAPI.getCompanyProfile(orgId)
            if (response.data) {
                set({ getCompanyProfileLoading: false, getCompanyProfileData: response.data })
                return response
            } else {
                set({ getCompanyProfileLoading: false, getCompanyProfileData: {} })
            }
        } catch (error) {
            set({ getCompanyProfileError: error.message || 'An error occurred', getCompanyProfileLoading: false })
        }
    },

    getCountry: async (data) => {
        set({ getCountryLoading: true })
        try {
            const response = await OnboardingAPI.getCountry(data)
            if (response) {
                set({ getCountryLoading: false, getCountryData: response.data })
                return response
            } else {
                set({ getCountryLoading: false, getCountryData: [] })
            }
        } catch (error) {
            set({ getCountryError: error.message || 'An error occurred', getCountryLoading: false })
        }
    },

    getState: async (id) => {
        set({ getStateLoading: true })
        try {
            const response = await OnboardingAPI.getState(id)
            if (response) {
                set({ getStateLoading: false, getStateData: response.data })
                return response
            } else {
                set({ getStateLoading: false, getStateData: [] })
            }
        } catch (error) {
            set({ getStateError: error.message || 'An error occurred', getStateLoading: false })
        }
    },

    getCity: async (id) => {
        set({ getCityLoading: true })
        try {
            const response = await OnboardingAPI.getCity(id)
            if (response) {
                set({ getCityLoading: false, getCityData: response.data })
                return response
            } else {
                set({ getCityLoading: false, getCityData: [] })
            }
        } catch (error) {
            set({ getCityError: error.message || 'An error occurred', getCityLoading: false })
        }
    }

})

export const useOnboardingStore = create(devtools(immer(OnboardingStore)))
export const useOnboardingStoreForSurvey = create(devtools(immer(OnboardingStoreForSurvey)))
export const useProfileScreenStore = create(devtools(immer(ProfileScreenStore)))
